/**
 * App Component
 *
 * Main application component with routing and authentication.
 * Phase 10: Frontend - Payment Integration
 *
 * CRITICAL CONSTRAINTS:
 * - NO localStorage/sessionStorage (Firebase Auth handles persistence)
 * - TypeScript strict mode (NO 'any' types)
 * - React Router for navigation
 * - AuthProvider, CreditsProvider, ToastProvider wrap entire app
 * - PayPalScriptProvider for payment integration
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { AuthProvider } from '@/contexts/AuthContext';
import { CreditsProvider } from '@/contexts/CreditsContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ToastContainer } from '@/components/ui';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { Login } from '@/pages/Login';
import { LandingPage } from '@/pages/LandingPage';
import { Signup } from '@/pages/Signup';
import { PasswordReset } from '@/pages/PasswordReset';
import { AboutUs } from '@/pages/AboutUs';
import { Contact } from '@/pages/Contact';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy';
import { TermsOfService } from '@/pages/TermsOfService';
import { Dashboard } from '@/pages/Dashboard';
import { Upload } from '@/pages/Upload';
import { Gallery } from '@/pages/Gallery';
import { Billing } from '@/pages/Billing';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { UserManagement } from '@/pages/admin/UserManagement';
import { PromptEditor } from '@/pages/admin/PromptEditor';


/**
 * App Routes Component
 * Separated to ensure useAuth is within AuthProvider
 */
function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      {/* Root - Landing Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <Upload />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gallery"
        element={
          <ProtectedRoute>
            <Gallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes (Super Admin Only) */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/prompts"
        element={
          <AdminRoute>
            <PromptEditor />
          </AdminRoute>
        }
      />



      {/* Catch-all - Redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main App Component
 */
function App(): React.JSX.Element {
  // PayPal configuration from environment variables
  const paypalOptions = {
    clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID || 'test',
    currency: 'CAD',
    intent: 'capture',
  };

  return (
    <BrowserRouter>
      <PayPalScriptProvider options={paypalOptions}>
        <AuthProvider>
          <CreditsProvider>
            <ToastProvider>
              <AppRoutes />
              <ToastContainer />
            </ToastProvider>
          </CreditsProvider>
        </AuthProvider>
      </PayPalScriptProvider>
    </BrowserRouter>
  );
}

export default App;
